import { cloneElement } from 'react'
import type { ReactElement } from 'react'
import { describedBy, Field } from '@/ui/Field'

type ControlProps = {
  'aria-describedby'?: string
  'aria-invalid'?: boolean
  'aria-required'?: boolean
}

type Props = {
  /** Must match the id of the control rendered as children. */
  htmlFor: string
  label: string
  error?: string
  required?: boolean
  className?: string
  children: ReactElement<ControlProps>
}

/**
 * Label above a control, validation message below it.
 * Wires `aria-describedby` and `aria-invalid` onto the control itself, so a call site
 * cannot forget them and controls style their error state off `aria-invalid`.
 */
export function FormField({ htmlFor, label, error, required, className, children }: Props) {
  const control = cloneElement(children, {
    'aria-describedby': describedBy(htmlFor, error, children.props['aria-describedby']),
    'aria-invalid': error ? true : children.props['aria-invalid'],
    'aria-required': required ? true : children.props['aria-required'],
  })

  return (
    <Field fieldId={htmlFor} error={error} reserveErrorSpace className={className}>
      <div className="flex items-baseline">
        <label htmlFor={htmlFor} className="text-label text-neutral-800">
          {label}
        </label>
        {required ? (
          <span aria-hidden="true" className="ms-1 text-label text-error-600">
            *
          </span>
        ) : null}
      </div>
      {control}
    </Field>
  )
}
